import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes, Link, NavLink, useParams, useNavigate } from 'react-router-dom';
import './App.css';
import { ClusterFullImage, ClusterThumbnails, Cluster_overview as ClusterOverview } from './Cluster'
import { Map } from './map'
import BlogPage from './BlogPage';
import Lab from './Lab';
import Drop from './Drop';
import Describe from './Describe';
import DescribePanel from './DescribePanel';
import SiteNav from './SiteNav';
import Home from './Home';
import Publications from './PublicationsPage';
import CV from './CV';
import Publish from './Publish';
import { API_BASE } from './apiBase';

// The CLIP+SAE panel under a gallery photo is off for now — it distracted from
// the image. Flip this to true to bring it back; everything else stays wired up
// (with it false the describe data isn't even fetched). The live /describe page
// is unaffected.
const SHOW_DESCRIBE_PANEL = false;

// Galleries come from the API so ones created in /publish show up in the
// sub-nav without a redeploy. The three original ones are always present.
let _galleriesPromise = null;
let _galleriesAt = 0;
const GALLERIES_TTL = 30000;   // so a gallery just made in /publish shows up

function loadGalleries() {
  if (!_galleriesPromise || Date.now() - _galleriesAt > GALLERIES_TTL) {
    _galleriesAt = Date.now();
    _galleriesPromise = fetch(`${API_BASE}/galleries`)
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []);
  }
  return _galleriesPromise;
}

function useGalleries() {
  const [list, setList] = useState([]);
  useEffect(() => { let on = true; loadGalleries().then((g) => { if (on) setList(g); }); return () => { on = false; }; }, []);
  return list;
}

// Lazy-load the precomputed gallery describe+explain map once, keyed by gallery id
// (e.g. "003.jpg"). Served by the gallery API so it stays in step with the
// images when the publish tool renumbers them.
let _galleryDesc = null;
let _galleryDescPromise = null;
function loadGalleryDesc() {
  if (_galleryDesc) return Promise.resolve(_galleryDesc);
  if (!_galleryDescPromise) {
    _galleryDescPromise = fetch(`${API_BASE}/gallery_describe.json`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((j) => { _galleryDesc = j; return j; })
      .catch(() => ({}));
  }
  return _galleryDescPromise;
}
function useGalleryDescribe(key) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let on = true;
    if (!key) { setData(null); return; }
    loadGalleryDesc().then((j) => { if (on) setData(j[key] || null); });
    return () => { on = false; };
  }, [key]);
  return data;
}

function App() {
  const [darkMode] = useState(true);

  return (
    <Router>
      <div className={`App ${darkMode ? 'dark' : ''}`}>
        <SiteNav />
        <Routes>
          {/* Personal site */}
          <Route path="/" element={<Home />} />
          <Route path="/publications" element={<Publications />} />
          <Route path="/cv" element={<CV />} />

          {/* Gallery subsite */}
          <Route path="/gallery" element={<GalleryView k="main" endpoint="/get_thumbnails" basePath="/gallery" layout="standard" />} />
          <Route path="/gallery/image/:index" element={<ConnectedFullImage endpoint="/get_thumbnails" basePath="/gallery" />} />
          <Route path="/gallery/1x1" element={<GalleryView k="1x1" endpoint="/get_thumbnails_1x1" basePath="/gallery/1x1" layout="large" />} />
          <Route path="/gallery/1x1/image/:index" element={<ConnectedFullImage endpoint="/get_thumbnails_1x1" basePath="/gallery/1x1" />} />
          <Route path="/gallery/13x7" element={<GalleryView k="13x7" endpoint="/get_thumbnails_13x7" basePath="/gallery/13x7" layout="single" />} />
          <Route path="/gallery/13x7/image/:index" element={<ConnectedFullImage endpoint="/get_thumbnails_13x7" basePath="/gallery/13x7" />} />
          {/* galleries created in /publish */}
          <Route path="/gallery/g/:gid" element={<CustomGallery />} />
          <Route path="/gallery/g/:gid/image/:index" element={<CustomGalleryImage />} />

          {/* Other Routes */}
          <Route path="/map" element={<Map />} />
          <Route path="/clusters" element={<ClusterOverview />} />
          <Route path="/cluster/:name" element={<ClusterThumbnails />} />
          <Route path="/cluster/:name/image/:index" element={<ClusterFullImage />} />
          <Route path="/similar-image/:index" element={<SimilarImageFullView />} />
          <Route path="/blog-page" element={<BlogPage />} />
          {/* Private, unlisted: local-model chat (see src/Lab.js) */}
          <Route path="/lab" element={<Lab />} />
          <Route path="/publish" element={<Publish />} />
          {/* Private, unlisted: secure file drop to the Mac (see src/Drop.js) */}
          <Route path="/drop" element={<Drop />} />
          {/* Private, unlisted: live CLIP+SAE photo describe/explain (see src/Describe.js) */}
          <Route path="/describe" element={<Describe />} />
          <Route path="/about" element={<About />} />

          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutzerklaerung" element={<Datenschutzerklaerung />} />
        </Routes>
      </div>
    </Router>
  );
}

function CustomGallery() {
  const { gid } = useParams();
  const galleries = useGalleries();
  const conf = galleries.find((g) => g.id === gid);
  // Panoramas get a row each. Squarer formats tile, with the column width set
  // from the ratio so every row lands at roughly the same height whatever the
  // shape — a 3:2 gets wider columns than a square, but not so wide it drops to
  // one per row.
  const ratio = (conf && conf.aspect) || 1;
  const ROW_HEIGHT = 340;
  const tileMin = Math.round(Math.min(900, Math.max(300, ROW_HEIGHT * ratio)));
  return (
    <GalleryView k={gid} endpoint={`/g/${gid}/list`} basePath={`/gallery/g/${gid}`}
                 layout={conf && conf.layout === 'single' ? 'single' : 'auto'}
                 tileMin={tileMin} />
  );
}

function CustomGalleryImage() {
  const { gid } = useParams();
  return <ConnectedFullImage endpoint={`/g/${gid}/list`} basePath={`/gallery/g/${gid}`} />;
}

function GalleryView({ endpoint, basePath, layout, k, tileMin }) {
  const galleries = useGalleries();
  const tab = ({ isActive }) => 'gallery-tab' + (isActive ? ' active' : '');
  return (
    <div className="gallery-section">
      <div className="gallery-subnav">
        <NavLink to="/gallery" end className={tab}>All</NavLink>
        {galleries.filter((g) => g.id !== 'gallery').map((g) => (
          <NavLink key={g.id} to={g.path} className={tab}>{g.label}</NavLink>
        ))}
      </div>
      <GalleryPage key={k} endpoint={endpoint} basePath={basePath} layout={layout} tileMin={tileMin} />
    </div>
  );
}

function GalleryPage({ endpoint, basePath, layout, tileMin }) {
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(true); // Track loading state

  useEffect(() => {
    setLoading(true); // Ensure loading is true when endpoint changes
    axios.get(`${API_BASE}${endpoint}`)
      .then(response => {
        setThumbnails(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching thumbnails:', error);
        setLoading(false);
      });
  }, [endpoint]);

  // If loading or no thumbnails, show empty page (prevents flash of old content)
  if (loading || thumbnails.length === 0) {
    return <div style={{ minHeight: '100vh' }}></div>;
  }

  return <ThumbnailGrid thumbnails={thumbnails} basePath={basePath} layout={layout} tileMin={tileMin} />;
}

function ConnectedFullImage({ endpoint, basePath }) {
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}${endpoint}`)
      .then(response => {
        setThumbnails(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching thumbnails:', error);
        setLoading(false);
      });
  }, [endpoint]);

  if (loading) return <div style={{marginTop: '50px'}}>Loading...</div>;

  if (thumbnails.length === 0) return <div>No images found.</div>;

  return <FullImage thumbnails={thumbnails} basePath={basePath} />;
}

function About() {
  return (
    <div className="about-page">
      <h1>About This Site</h1>
      <p>Hi, this is my website with my images, I captured over the years. In the near future, this site will be supported
        by some machine learning algorithms I am developing to sort, search and generate images.</p>
    </div>
  );
}

function Impressum() {
  return (
    <div>
      <h1>Impressum</h1>
      <p>Fabian Morelli<br />Im Baeumle 9<br />75242 Neuhausen</p>
      <h2>Kontakt</h2>
      <p>E-Mail: fabian.morelli@web.de</p>
    </div>
  );
}

function Datenschutzerklaerung() {
  return (
    <div>
      <h1>Datenschutz&shy;erkl&auml;rung</h1>
      <h2>1. Datenschutz auf einen Blick</h2>
      <p>...</p>
    </div>
  );
}

function ThumbnailGrid({ thumbnails, basePath, layout, tileMin }) {
  const getLink = (index) => basePath ? `${basePath}/image/${index}` : `/image/${index}`;

  let gridClass = "thumbnail-grid masonry";
  if (layout === 'large') {
    gridClass += " large-grid";
  } else if (layout === 'single') {
    gridClass += " single-column";
  } else if (layout === 'auto') {
    gridClass += " auto-grid";      // column width comes from the format's shape
  }

  const useFullImages = layout === 'large' || layout === 'single' || layout === 'auto';

  return (
    <div className={gridClass}
         style={tileMin ? { '--tile-min': `${tileMin}px` } : undefined}>
      {thumbnails.map((thumbnailPath, index) => {
        let imageSrc = `${API_BASE}${thumbnailPath}`;
        // the 1x1/13x7 grids display the images themselves; custom galleries
        // already hand back thumbnail paths, so leave those alone
        if (useFullImages && !thumbnailPath.startsWith('/g/')) {
          imageSrc = imageSrc.replace('get_thumbnail', 'get_image');
        }

        return (
          <div key={index} className="masonry-item">
            <Link to={getLink(index)} style={{ textDecoration: 'none', outline: 'none', boxShadow: 'none' }}>
              <img
                src={imageSrc}
                alt={`Gallery item ${index}`}
                style={{ border: 'none', outline: 'none' }}
              />
            </Link>
          </div>
        );
      })}
    </div>
  );
}

function FullImage({ thumbnails, basePath }) {
  const { index } = useParams();
  const navigate = useNavigate();
  const currentIndex = parseInt(index, 10);

  const [similarThumbnails, setSimilarThumbnails] = useState([]);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const navigateTo = useCallback((newIndex) => {
     if (basePath) {
        navigate(`${basePath}/image/${newIndex}`);
     } else {
        navigate(`/image/${newIndex}`);
     }
  }, [basePath, navigate]);

  const nextImage = useCallback(() => {
    if (currentIndex < thumbnails.length - 1) {
      navigateTo(currentIndex + 1);
      setSimilarThumbnails([]);
    }
  }, [currentIndex, thumbnails.length, navigateTo]);

  const prevImage = useCallback(() => {
    if (currentIndex > 0) {
      navigateTo(currentIndex - 1);
      setSimilarThumbnails([]);
    }
  }, [currentIndex, navigateTo]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextImage();
    }
    if (isRightSwipe) {
      prevImage();
    }
  };

  const handleThumbnailClick = (similarIndex) => {
    navigate(`/similar-image/${similarIndex}`);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        nextImage();
      } else if (event.key === 'ArrowLeft') {
        prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [nextImage, prevImage]);

  // strip the ?v=<revision> cache-buster before using the filename as a key
  const descKey = SHOW_DESCRIBE_PANEL && thumbnails[currentIndex]
    ? thumbnails[currentIndex].split('?')[0].split('/').pop()
    : null;
  const describeData = useGalleryDescribe(descKey);

  const thumbnail = thumbnails[currentIndex];

  if (!thumbnail) return <div>Image not found</div>;

  const fullImageUrl = `${API_BASE}${thumbnail
    .replace('thumbnails', 'images')
    .replace('get_thumbnail', 'get_image')
    .replace('/thumb/', '/image/')}`;

  return (
    <>
    <div
      className="full-image-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <img
        src={fullImageUrl}
        alt="Full size"
        className={`full-image ${similarThumbnails.length > 0 ? 'scaled-down' : ''}`}
      />
      {similarThumbnails.length > 0 && (
        <div className="similar-thumbnails">
          {similarThumbnails.map((similarThumbnail, idx) => {
            const similarIndex = similarThumbnail.split('/').pop();
            return (
              <img
                key={idx}
                src={`${API_BASE}${similarThumbnail}`}
                alt="Similar thumbnail"
                className="thumbnail"
                onClick={() => handleThumbnailClick(similarIndex)}
              />
            );
          })}
        </div>
      )}
    </div>
    {describeData && <DescribePanel data={describeData} />}
    </>
  );
}

function SimilarImageFullView() {
  const { index } = useParams();
  const fullImageUrl = `${API_BASE}/get_image_rest/${index}`;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        window.history.back();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="full-image-container">
      <img src={fullImageUrl} alt="Full size" className="full-image" />
    </div>
  );
}

export default App;